import info from "../Info";
import experiences from "../experience/experiences";
import projects from "../projects/projects";
import getGroupedSkills from "../../utils/getUniqueSkills";

export const getProjectFilename = (projectName) =>
  projectName.split(" ")[0].toLowerCase().replace(/[^a-z0-9]/g, "") + ".txt";

// Include the start year so duplicate companies (e.g. two UTD entries) stay unique.
export const getExperienceFilename = (companyName, start = "") => {
  const slug = companyName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  const year = start ? `-${start.replace(/[^0-9]/g, "").slice(-4)}` : "";
  return slug + year + ".txt";
};

// Detailed project write-ups keyed by the first word of the project name (lowercased).
const projectDetails = {
  axis: [
    "Axis is an AI-powered college compendium built to help high school",
    "students navigate the overwhelming landscape of post-secondary",
    "education. The platform aggregates data on 5,000+ colleges and",
    "30,000+ scholarships into a single searchable interface.",
    "",
    "Architecture:",
    "  Backend  — Java Spring Boot (Maven) REST API serving college,",
    "             scholarship, and user data from a PostgreSQL database.",
    "  Frontend — Angular SPA with TypeScript, featuring search filters,",
    "             saved lists, and a personalized dashboard.",
    "  AI Layer — Claude-powered essay feedback system that reviews",
    "             drafts and returns structured suggestions.",
    "  Storage  — AWS S3 for user-uploaded documents (transcripts,",
    "             essays, recommendation letters).",
    "",
    "Key contributions:",
    "  - Designed the data pipeline that ingested and normalized college",
    "    datasets from multiple public sources into PostgreSQL.",
    "  - Built the AI essay feedback flow: users upload a draft, the",
    "    backend sends it to Claude with a rubric-aware prompt, and the",
    "    response is streamed back to the frontend in real time.",
    "  - Implemented role-based access so counselors can view and",
    "    annotate student profiles.",
  ],
  speechability: [
    "Speechability is an accessibility-focused application that turns",
    "spoken voice commands into executable actions. The goal was to",
    "improve hands-free interaction for users with motor impairments.",
    "",
    "Technical approach:",
    "  - Built a custom command matching module in Python that maps",
    "    fuzzy speech-to-text output to a predefined command set.",
    "  - Implemented background noise mitigation using spectral gating,",
    "    cutting per-interaction latency by up to 3 seconds.",
    "  - Achieved a ~24% improvement in voice command execution",
    "    accuracy through iterative tuning of confidence thresholds",
    "    and phonetic similarity scoring.",
    "",
    "Development process:",
    "  - Worked in an agile team with two-week sprints, daily standups,",
    "    and demo days for stakeholder feedback.",
    "  - Authored unit and integration tests for the recognition module",
    "    to ensure accuracy regressions were caught early.",
  ],
  seoptimize: [
    "SEOptimize is an AI-powered SaaS application that generates",
    "SEO-optimized business content — blog posts, product descriptions,",
    "meta tags — using OpenAI's GPT models.",
    "",
    "Architecture:",
    "  Frontend — Next.js React app with server-side rendering for fast",
    "             initial loads and good SEO (meta-ironic, right?).",
    "  Backend  — Python FastAPI service that orchestrates prompt",
    "             construction, calls the OpenAI API, and streams the",
    "             generated content back to the client.",
    "  Infra    — Dockerized and deployed serverlessly via AWS Lambda",
    "             behind API Gateway. The frontend is on Vercel.",
    "",
    "How it works:",
    "  1. User selects a content type and provides a topic / keywords.",
    "  2. The backend builds a structured prompt with SEO best practices",
    "     baked in (keyword density, heading hierarchy, meta length).",
    "  3. GPT generates the content; it's streamed to the UI in real",
    "     time so the user sees words appear as they're produced.",
    "  4. Output can be copied, downloaded, or regenerated with tweaks.",
    "",
    "Live: https://ai-saas-app-swart.vercel.app/",
  ],
  clientconnect: [
    "ClientConnect is a client management platform built for an",
    "organization serving 4,000+ clients. It replaced a patchwork of",
    "spreadsheets with a centralized web application.",
    "",
    "Architecture:",
    "  Frontend — Vue.js SPA with component-based UI, reactive data",
    "             binding, and Vuex for state management.",
    "  Backend  — RESTful API endpoints performing CRUD operations",
    "             against a PostgreSQL database.",
    "",
    "Key contributions:",
    "  - Built customer-tailored pages that dynamically render based on",
    "    client type, reducing the need for one-off custom views.",
    "  - Constructed API endpoints in JavaScript for creating, reading,",
    "    updating, and deleting client records with validation and",
    "    role-based access control.",
    "  - Led 5 collaborative sessions with the front-end team and the",
    "    client to assess requirements and iterate on designs, directly",
    "    increasing client satisfaction with the final product.",
  ],
  abimports: [
    "ABImports is a full-stack web application built for a local car",
    "importing business. It replaced their manual inventory tracking",
    "with a streamlined digital platform.",
    "",
    "Architecture:",
    "  Frontend — React SPA with efficient state management and",
    "             responsive design for desktop and mobile use.",
    "  Backend  — Express.js REST API handling vehicle CRUD operations.",
    "  Database — MySQL for structured vehicle and customer data.",
    "  Storage  — AWS S3 for vehicle images (multiple per listing).",
    "",
    "Key features:",
    "  - Vehicle inventory management with multi-image upload and",
    "    gallery view powered by S3 pre-signed URLs.",
    "  - Search and filter system for customers to browse available",
    "    imports by make, model, year, and price range.",
    "  - Admin dashboard for the business owner to manage listings,",
    "    track inquiries, and update vehicle status.",
  ],
};

// Detailed experience write-ups keyed by getExperienceFilename(company, start) minus ".txt".
const experienceDetails = {
  "paycom-2025": [
    "Software Development Intern at Paycom — Summer 2025",
    "Irving, TX",
    "",
    "Paycom is a cloud-based HCM (Human Capital Management) platform.",
    "Kevin worked on the sales-enablement team, building internal tools",
    "that helped reps locate and engage prospective clients.",
    "",
    "CRM Enrichment Microservice:",
    "  Designed and implemented a microservice that merged 20,000+",
    "  internal CRM records with 5,000+ Google Places API business data",
    "  points, improving the quality and reach of client outreach.",
    "",
    "High-Throughput Data Pipeline:",
    "  Reduced RESTful API response latency by 98.6% — from 22s to",
    "  ~300ms — by designing a pipeline with intelligent caching and",
    "  prefetching layers using C#, ASP .NET Core, SQL, and React.",
    "",
    "React SPA Performance:",
    "  Cut initial client load time by 60% via lazy loading, optimized",
    "  API response handling, and effective React state management and",
    "  memoization on the single-page application.",
    "",
    "Stack: C#, ASP .NET Core, MySQL, GCP Places API, React",
  ],
  "gather-(startup)-2023": [
    "Lead Software Engineer at Gather (Startup) — Nov 2023 to Aug 2024",
    "Dallas, TX",
    "",
    "Gather is a mobile event platform for college students, letting",
    "clubs and organizations post and discover campus events.",
    "",
    "Key contributions:",
    "  - Led the full product lifecycle: design, development, and",
    "    marketing of the event application.",
    "  - Grew the app to 5,000 downloads and 200,000 social media",
    "    views by driving organic growth campaigns.",
    "  - Implemented push notifications via Firebase Cloud Database",
    "    so users receive real-time alerts for nearby events, improving",
    "    engagement and retention.",
    "",
    "Stack: Firebase, React Native, JavaScript",
  ],
  "the-university-of-texas-at-dallas-2024": [
    "Software Engineer Intern at UT Dallas — Jan 2024 to May 2024",
    "Richardson, TX",
    "",
    "Worked on the Speechability project: an accessibility tool that",
    "enables hands-free computer operation through voice commands.",
    "",
    "Key contributions:",
    "  - Led a team to improve speech recognition accuracy by ~24% by",
    "    integrating an ML model that processes real-time audio input",
    "    and handles imprecise or noisy voice commands.",
    "  - Reduced user interaction latency by up to 3 seconds by",
    "    fine-tuning ML model parameters to filter noise and mitigate",
    "    system interference using Python.",
    "",
    "Stack: Python",
  ],
  "the-university-of-texas-at-dallas-2022": [
    "Software Engineer Intern at UT Dallas — Aug 2022 to May 2023",
    "Richardson, TX",
    "",
    "Worked on ClientConnect: a client management platform for an",
    "organization serving 4,000+ clients.",
    "",
    "Key contributions:",
    "  - Streamlined support for 4,000+ clients by developing REST API",
    "    endpoints and web pages using Vue.js, JavaScript, and",
    "    PostgreSQL.",
    "  - Increased client satisfaction by leading 5 collaborative",
    "    sessions with the client to gather requirements and iterate on",
    "    designs, helping the team ship a more accurate product.",
    "",
    "Stack: Vue.js, JavaScript, PostgreSQL",
  ],
};

const buildFileSystem = () => {
  // welcome.txt
  const welcomeContent = [
    "Welcome to kevin@portfolio.",
    "This terminal is a lightweight file system and AI assistant that represents Kevin Philip.",
    "",
    "Try these commands:",
    "- help",
    "- ls -a",
    "- cat about.txt",
    "- cat projects/seoptimize.txt",
    "",
    "Tip: Click any project or experience card to view it here.",
    "Tip: Ask me anything about Kevin's background, projects, and experience.",
  ].join("\n");

  // about.txt
  const aboutContent = [
    `Name: ${info.fname} ${info.lname}`,
    `Title: ${info.title}`,
    `Location: ${info.location}`,
    `Website: ${info.website}`,
    "",
    `"Passionate about building software that makes a difference."`,
  ].join("\n");

  // education.txt
  const educationContent = [
    `University: ${info.university}`,
    `Degree: ${info.degree}`,
    `GPA: ${info.gpa}`,
    `Graduation: ${info.graduationDate}`,
    "",
    `Masters: ${info.mastersDegree}`,
    `Masters Graduation: ${info.mastersGraduationDate}`,
  ].join("\n");

  // contact.txt
  const contactContent = [
    `Email: ${info.email}`,
    `Phone: ${info.phone}`,
    `LinkedIn: ${info.linkedin}`,
    `GitHub: ${info.github}`,
    `Website: ${info.website}`,
  ].join("\n");

  // skills.txt
  const grouped = getGroupedSkills();
  const skillsContent = Object.entries(grouped)
    .map(([category, skills]) => `${category}: ${skills.join(", ")}`)
    .join("\n");

  // experience directory
  const experienceChildren = {};
  experiences.forEach((exp) => {
    const filename = getExperienceFilename(exp.company, exp.start);
    const detailKey = filename.replace(".txt", "");
    const detailed = experienceDetails[detailKey];
    const content = detailed
      ? detailed.join("\n")
      : [
          `Company: ${exp.company}`,
          `Role: ${exp.positionName}`,
          `Location: ${exp.location}`,
          `Duration: ${exp.start} - ${exp.end}`,
          `Tech: ${exp.technologies}`,
          "",
          ...exp.desc.map((d) => `- ${d}`),
        ].join("\n");
    experienceChildren[filename] = { type: "file", content };
  });

  // projects directory
  const projectChildren = {};
  projects.forEach((proj) => {
    const filename = getProjectFilename(proj.name);
    const key = proj.name.split(" ")[0].toLowerCase().replace(/[^a-z0-9]/g, "");
    const detailed = projectDetails[key];
    const content = detailed
      ? [
          `=== ${proj.name} ===`,
          `Tech: ${proj.tech}`,
          `Date: ${proj.date}`,
          ...(proj.link ? [`Live: ${proj.link}`] : []),
          ...(proj.github ? [`GitHub: ${proj.github}`] : []),
          "",
          ...detailed,
        ].join("\n")
      : [
          `Project: ${proj.name}`,
          `Tech: ${proj.tech}`,
          `Date: ${proj.date}`,
          ...(proj.link ? [`Link: ${proj.link}`] : []),
          ...(proj.github ? [`GitHub: ${proj.github}`] : []),
          "",
          ...proj.desc.map((d) => `- ${d}`),
        ].join("\n");
    projectChildren[filename] = { type: "file", content };
  });

  return {
    type: "dir",
    children: {
      "welcome.txt": { type: "file", content: welcomeContent },
      "about.txt": { type: "file", content: aboutContent },
      "education.txt": { type: "file", content: educationContent },
      "contact.txt": { type: "file", content: contactContent },
      "skills.txt": { type: "file", content: skillsContent },
      experience: { type: "dir", children: experienceChildren },
      projects: { type: "dir", children: projectChildren },
      ".easter-eggs": {
        type: "dir",
        children: {
          "joke.txt": {
            type: "file",
            content: "Why do programmers prefer dark mode?\nBecause light attracts bugs.",
          },
          "hobbies.txt": {
            type: "file",
            content: [
              "When I'm not coding, you can find me:",
              "- Playing basketball",
              "- Exploring new coffee shops",
              "- Binge-watching sci-fi shows",
              "- Tinkering with side projects at 2am",
            ].join("\n"),
          },
          "secret.txt": {
            type: "file",
            content: "You found the secret file! You're clearly someone who explores.\nI like that. Let's connect — thekevinphilip@gmail.com",
          },
        },
      },
    },
  };
};

export const resolvePath = (fileSystem, pathParts) => {
  let current = fileSystem;
  for (const part of pathParts) {
    if (part === "" || part === ".") continue;
    if (!current || current.type !== "dir" || !current.children[part]) {
      return null;
    }
    current = current.children[part];
  }
  return current;
};

export const normalizePath = (currentPath, targetPath) => {
  let parts;
  if (targetPath.startsWith("~") || targetPath.startsWith("/")) {
    parts = targetPath.replace("~", "").split("/").filter(Boolean);
  } else {
    parts = [...currentPath, ...targetPath.split("/").filter(Boolean)];
  }

  const resolved = [];
  for (const part of parts) {
    if (part === "..") {
      resolved.pop();
    } else if (part !== ".") {
      resolved.push(part);
    }
  }
  return resolved;
};

export default buildFileSystem;
